import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'
import { useQueryClient } from '@tanstack/react-query'

import { TeamMemberRole } from '@makyoapp/makyo-sdk/models/ACL'
import { MemberAdapter, TeamAdapter } from '@makyoapp/makyo-common'

import { queryKeys } from '@/lib/react-query'
import { toasted } from '@/lib/toast'
import { useAddMembersMutation } from '@/resolvers/org/mutation'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useReqError } from '@/hooks/useReqError'

/**
 * @function useAddTeamMemberModal
 * @description A custom React Hook to handle the logic for adding new team members in a modal.
 * @param {TeamAdapter} team - The current team.
 * @param {() => void} onBeforeUpdate - Function to execute before updating.
 * @param {() => void} onAfterUpdate - Function to execute after updating.
 * @returns {Object} Various states and handlers:
 *  - {boolean} showModal - A state to control the visibility of the modal.
 *  - {Array} addNewMembers - An array state holding the new members to be added.
 *  - {Array} memberOptions - An array state holding the options for members.
 *  - {string} currentOrgId - A state holding the id of the current organization.
 *  - {boolean} isBtnLoading - A state to control the loading state of the button.
 *  - {function} resetModal - A function to reset the modal by hiding it and clearing the new members array.
 *  - {function} setShowModal - A function to set the visibility of the modal.
 *  - {function} handleCheck - A function to handle the checking of a member. It either adds or removes the member from the new members array.
 *  - {function} handleCheckAll - A function to handle the checking of all members. It either adds all members to or clears the new members array.
 *  - {function} handleAddMemberRole - A function to handle the adding of a role to a member.
 *  - {function} handleSaveMember - A function to handle the saving of a member. It calls a mutation function to add the new members and handles the loading state of the button and any API errors.
 *  - {function} t - A function from the 'react-i18next' library for language translation.
 */
const useAddTeamMemberModal = (team: TeamAdapter) => {
  const queryClient = useQueryClient()

  // Using translation hook
  const { t } = useTranslation()

  // Using context to get and update the current organization
  const { currentOrg } = useCurrentOrgContext()

  // Fetching current organization ID
  const currentOrgId = currentOrg?.id || ''

  // Fetching team ID
  const teamId = team.id

  // Setting up state for modal visibility
  const [showModal, setShowModal] = useBoolean(false)

  // Function to reset modal state
  const resetModal = () => {
    setShowModal(false)
    setAddNewMembers([])
  }

  // Setting up state for member options
  const [memberOptions, setMemberOptions] = useState<MemberAdapter[]>([])

  // Setting up state for new members to be added
  const [addNewMembers, setAddNewMembers] = useState<
    {
      member: MemberAdapter
      teamRole: TeamMemberRole
    }[]
  >([])

  // Setting up state for button loading status
  const [isBtnLoading, setIsBtnLoading] = useBoolean(false)

  // Using error handling hook
  const printAPIError = useReqError()

  // Define Mutations
  const { mutate: addMembersMutation } = useAddMembersMutation()

  // Filtering member options
  const filterMemberOptions = (members: MemberAdapter[], teamId: string) => {
    return members.filter((m) => {
      return m.status === 'active' && m.teams.filter((mt) => mt.id === teamId).length < 1
    })
  }

  // Effect to set member options based on team Id and current organization
  useEffect(() => {
    if (teamId && currentOrg) {
      setMemberOptions(filterMemberOptions(currentOrg.members, teamId))
    }
  }, [teamId, currentOrg, setMemberOptions])

  /**
   * This function handles the event when all members are selected or deselected
   * from a list.
   *
   * @param {boolean} isChecked - Flag indicating whether all members are selected.
   * If true, it sets all members to the viewer role.
   * If false, it removes all members from the current selection.
   */
  const handleCheckAll = (isChecked: boolean) => {
    if (isChecked) {
      setAddNewMembers(
        memberOptions.map((member) => {
          return {
            member,
            teamRole: TeamMemberRole.Viewer,
          }
        })
      )
    } else {
      setAddNewMembers([])
    }
  }

  /**
   * This function handles the event when an individual member is selected or
   * deselected from a list.
   *
   * @param {boolean} isChecked - Flag indicating whether a member is selected.
   * @param {MemberAdapter} member - The member object being checked/unchecked.
   * If the member is selected (isChecked = true), the member is added to the
   * addNewMembers array with the role of Viewer.
   * If the member is deselected (isChecked = false), the member is removed from
   * the addNewMembers array.
   */
  const handleCheck = (isChecked: boolean, member: MemberAdapter) => {
    if (isChecked) {
      setAddNewMembers([
        ...addNewMembers,
        {
          member,
          teamRole: TeamMemberRole.Viewer,
        },
      ])
    } else {
      setAddNewMembers(addNewMembers.filter((m) => m.member.id !== member.id))
    }
  }

  /**
   * This function handles the event of adding a role to a member.
   *
   * @param {TeamMemberRole.Viewer} opt - The role being assigned to the member.
   * @param {MemberAdapter} member - The member object being assigned a role.
   * This function finds the member in the addNewMembers array and assigns the
   * selected role to that member. It then updates the addNewMembers state.
   */
  const handleAddMemberRole = (opt: TeamMemberRole.Viewer, member: MemberAdapter) => {
    const newMembers = [...addNewMembers]
    const idx = newMembers.findIndex((nm) => nm.member.id === member.id)
    if (idx !== -1) {
      newMembers[idx].teamRole = opt as TeamMemberRole
    }
    setAddNewMembers(newMembers)
  }

  /**
   * This function handles the event of saving the member's information.
   * It starts by setting the IsBtnLoading state to true to show loading on the button.
   * It then calls the addMembersMutation function with the new member's information.
   *
   * On successful mutation, a success message is displayed and the organization information
   * is updated.
   *
   * On error, the error message is printed.
   *
   * When the mutation is settled, the modal is reset and the loading state of the button is removed.
   */
  const handleSaveMember = () => {
    setIsBtnLoading(true)
    addMembersMutation(
      {
        teamId,
        newMembers: addNewMembers,
      },
      {
        onSuccess() {
          queryClient.invalidateQueries({
            queryKey: [queryKeys.orgTeams, currentOrgId],
          })

          toasted('New member added!')
        },
        onError(e) {
          printAPIError(e)
        },
        onSettled() {
          resetModal()
          setIsBtnLoading(false)
        },
      }
    )
  }

  return {
    //states
    showModal,
    addNewMembers,
    memberOptions,
    currentOrgId,
    isBtnLoading,

    //methods
    resetModal,
    setShowModal,
    handleCheck,
    handleCheckAll,
    handleAddMemberRole,
    handleSaveMember,
    t,
  }
}

export default useAddTeamMemberModal
